/**
 * PrayerWeatherIntelligence — synthesis layer combining prayer times with
 * Open-Meteo hourly forecast to recommend "outdoor windows" for the day.
 *
 * Spec: services/dispatch/dev-minipc1/dev-minipc1_Prompt_2026-05-06_23-00_tier2-17-aweni-prayer-weather-intel.md
 *
 * Algorithm — outdoor desirability per hour:
 *   score(hour) = 100
 *     - max(0, 22 - temp_c) * 2          // cold penalty (below 22°C)
 *     - max(0, temp_c - 26) * 3          // hot penalty (above 26°C)
 *     - max(0, uv_index - 5) * 6         // UV penalty (above 5)
 *     - precipitation_prob_pct * 0.8     // rain probability penalty
 *     - max(0, wind_kmh - 20) * 1.5      // strong wind penalty
 *     - max(0, aqi - 50) * 0.3           // air quality penalty
 *     - (is_night ? 30 : 0)              // night discount
 *
 * Then we group consecutive hours into windows (≥60 min) constrained to
 * lie BETWEEN two prayer events (so the user's outdoor window doesn't
 * cross a prayer obligation), and rank by mean score.
 *
 * Produces up to 3 named windows:
 *   - "Best walk window" — highest mean score
 *   - "Best stroller window" — best score with temp 18–25°C, UV ≤4
 *     (gentle conditions for kids); skipped if no eligible window exists
 *   - "Best run window" — best score with temp 12–22°C (cooler is better
 *     for cardiovascular effort); skipped if no eligible window exists
 *
 * Hydration nudge:
 *   - When daily max temp >30°C AND UV peak >7 within daylight hours,
 *     adds a nudge tied to the next pre-Dhuhr hour: "Drink before Dhuhr".
 */

import type { LocationState, WeatherDetails } from '../modules/home/types';
import { prayerTimeService } from './PrayerTimeService';

export type WindowKind = 'walk' | 'stroller' | 'run';

export interface OutdoorWindow {
  kind: WindowKind;
  startIso: string; // ISO timestamp for the start
  endIso: string;
  startHour: number; // local hour 0-23
  endHour: number;
  durationMin: number;
  meanScore: number;
  meanTemp: number;
  meanWind: number;
  meanUv: number;
  rainPct: number;
  /** Localized titles built later via i18n (the service is pure logic). */
  precedingPrayer: 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha' | null;
  followingPrayer: 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha' | null;
  /** Compact rationale tag for telemetry. */
  reasonTag: string;
}

export interface OutdoorIntel {
  generatedAt: number;
  windows: OutdoorWindow[];
  /** Hydration nudge fires on hot+UV days. */
  hydrationNudge: { fire: boolean; before?: 'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha'; tempPeak: number; uvPeak: number };
  /** Whether the day is "indoor day" — no window scored ≥40. */
  indoorDay: boolean;
  /** Used by AnticipationEngine and the OutdoorWindows widget. */
  city: string | null;
}

const PRAYER_KEYS: Array<'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha'> = [
  'fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha',
];

interface HourlyPoint {
  hour: number; // 0-23
  iso: string;
  temp: number;
  rainProb: number;
  wind: number;
  uv: number;
  weatherCode: number;
  aqi: number;
}

/**
 * Compute the desirability score for a given hour.
 * Pure function — easily unit-testable.
 */
export function scoreHour(p: HourlyPoint): number {
  let s = 100;
  if (p.temp < 22) s -= (22 - p.temp) * 2;
  if (p.temp > 26) s -= (p.temp - 26) * 3;
  if (p.uv > 5) s -= (p.uv - 5) * 6;
  s -= p.rainProb * 0.8;
  if (p.wind > 20) s -= (p.wind - 20) * 1.5;
  if (p.aqi > 50) s -= (p.aqi - 50) * 0.3;
  // Night hours (defined here as 21-04 local) — outdoor activity less safe.
  if (p.hour >= 21 || p.hour < 5) s -= 30;
  return Math.max(0, Math.round(s));
}

function isStrollerFriendly(p: HourlyPoint): boolean {
  return p.temp >= 18 && p.temp <= 25 && p.uv <= 4 && p.rainProb < 30;
}
function isRunFriendly(p: HourlyPoint): boolean {
  return p.temp >= 12 && p.temp <= 22 && p.rainProb < 40 && p.wind < 25;
}

/**
 * Collapse hourly points into contiguous "good-enough" windows ≥ minMin.
 * `predicate` chooses which hours to include.
 */
function collapseWindows(
  points: HourlyPoint[],
  predicate: (p: HourlyPoint, score: number) => boolean,
  minMin = 60,
): Array<{ start: number; end: number; points: HourlyPoint[] }> {
  const out: Array<{ start: number; end: number; points: HourlyPoint[] }> = [];
  let cur: HourlyPoint[] = [];
  for (const p of points) {
    const s = scoreHour(p);
    if (predicate(p, s)) {
      cur.push(p);
    } else if (cur.length > 0) {
      if (cur.length * 60 >= minMin) {
        out.push({ start: cur[0].hour, end: cur[cur.length - 1].hour + 1, points: cur });
      }
      cur = [];
    }
  }
  if (cur.length > 0 && cur.length * 60 >= minMin) {
    out.push({ start: cur[0].hour, end: cur[cur.length - 1].hour + 1, points: cur });
  }
  return out;
}

function meanOf(arr: number[]): number { return arr.reduce((s, v) => s + v, 0) / Math.max(1, arr.length); }

function pickPrayer(hour: number, prayerHours: Record<string, number>): { preceding: OutdoorWindow['precedingPrayer']; following: OutdoorWindow['followingPrayer'] } {
  const ordered = PRAYER_KEYS
    .map((k) => ({ k, h: prayerHours[k] }))
    .filter((x) => Number.isFinite(x.h))
    .sort((a, b) => a.h - b.h);
  let preceding: OutdoorWindow['precedingPrayer'] = null;
  let following: OutdoorWindow['followingPrayer'] = null;
  for (const { k, h } of ordered) {
    if (h <= hour) preceding = k as OutdoorWindow['precedingPrayer'];
    if (h > hour && following === null) following = k as OutdoorWindow['followingPrayer'];
  }
  return { preceding, following };
}

function buildWindow(
  kind: WindowKind,
  group: { start: number; end: number; points: HourlyPoint[] },
  prayerHours: Record<string, number>,
): OutdoorWindow {
  const scores = group.points.map(scoreHour);
  const startIso = group.points[0].iso;
  const endIso = group.points[group.points.length - 1].iso;
  const dh = pickPrayer(group.start, prayerHours);
  return {
    kind,
    startIso,
    endIso,
    startHour: group.start,
    endHour: group.end,
    durationMin: group.points.length * 60,
    meanScore: Math.round(meanOf(scores)),
    meanTemp: Math.round(meanOf(group.points.map((p) => p.temp))),
    meanWind: Math.round(meanOf(group.points.map((p) => p.wind))),
    meanUv: Math.round(meanOf(group.points.map((p) => p.uv))),
    rainPct: Math.round(Math.max(...group.points.map((p) => p.rainProb))),
    precedingPrayer: dh.preceding,
    followingPrayer: dh.following,
    reasonTag: `${kind}:${group.points[0].hour}-${group.points[group.points.length - 1].hour + 1}`,
  };
}

/**
 * The main public entry. Returns a synchronous result given the inputs;
 * the caller (widget) is responsible for fetching weather details + prayer
 * times before calling.
 */
export function computeOutdoorIntel(
  details: WeatherDetails | null,
  location: LocationState,
): OutdoorIntel {
  const empty: OutdoorIntel = {
    generatedAt: Date.now(),
    windows: [],
    hydrationNudge: { fire: false, tempPeak: 0, uvPeak: 0 },
    indoorDay: true,
    city: location.city,
  };
  if (!details || !details.hourly || details.hourly.length === 0) return empty;

  // Build the same-day hourly array (only future hours from 'now', plus we
  // include a small overlap of past hours for context — these are scored
  // but excluded from window selection).
  const todayLocal = new Date();
  const todayDateStr = todayLocal.toISOString().slice(0, 10);
  const points: HourlyPoint[] = [];
  for (const h of details.hourly) {
    const date = new Date(h.time);
    if (Number.isNaN(date.getTime())) continue;
    // Only same calendar day in the user's tz (we use the device tz here —
    // good enough for "today's outdoor window"; cross-tz subtleties matter
    // only for travel use-case which already has a separate widget).
    if (date.toISOString().slice(0, 10) !== todayDateStr) continue;
    points.push({
      hour: date.getHours(),
      iso: h.time,
      temp: h.temp,
      rainProb: h.precipitationProbability,
      wind: h.wind,
      uv: 0, // hourly UV is not in the hourly array; daily UV peak is used for nudge below
      weatherCode: h.weatherCode,
      aqi: 0,
    });
  }

  // Inject UV peak from daily forecast (nudge logic only). For per-hour UV
  // we approximate using the daily peak around solar noon (12-15 local) —
  // the rest gets 0, which over-rewards midday hours; we mitigate via the
  // explicit hot-temp penalty so the score still stays balanced.
  const dailyToday = details.daily?.[0];
  const uvPeak = dailyToday?.uvMax ?? 0;
  const tempPeak = dailyToday?.tempMax ?? 0;
  for (const p of points) {
    if (p.hour >= 11 && p.hour <= 15) p.uv = uvPeak;
    else if (p.hour >= 9 && p.hour <= 17) p.uv = uvPeak * 0.6;
  }
  // AQI: Open-Meteo air-quality lives in details.airQuality (single current value)
  const aqi = details.airQuality?.aqiEuropean ?? 0;
  for (const p of points) p.aqi = aqi;

  // Build prayer-hour map (decimal hours since midnight) so we can name
  // each window by its bracketing prayers.
  const todayTimes = prayerTimeService.getTodayTimes();
  const prayerHours: Record<string, number> = {};
  if (todayTimes) {
    for (const k of PRAYER_KEYS) {
      const d = todayTimes[k];
      if (d instanceof Date && !Number.isNaN(d.getTime())) {
        prayerHours[k] = d.getHours() + d.getMinutes() / 60;
      }
    }
  }

  // Filter to "now and later" — past hours are still useful for scoring
  // context but a window must include current or future time.
  const nowHour = todayLocal.getHours();
  const futureish = points.filter((p) => p.hour >= Math.max(0, nowHour - 1));

  // 1) Walk window — best-of-day overall (predicate: score ≥ 50)
  const walkGroups = collapseWindows(futureish, (_p, s) => s >= 50);
  const walkBest = walkGroups
    .map((g) => buildWindow('walk', g, prayerHours))
    .sort((a, b) => b.meanScore - a.meanScore)[0] || null;

  // 2) Stroller window — gentler conditions, score ≥ 55
  const strollerGroups = collapseWindows(
    futureish,
    (p, s) => s >= 55 && isStrollerFriendly(p),
  );
  const strollerBest = strollerGroups
    .map((g) => buildWindow('stroller', g, prayerHours))
    .sort((a, b) => b.meanScore - a.meanScore)[0] || null;

  // 3) Run window — cool conditions, score ≥ 50
  const runGroups = collapseWindows(
    futureish,
    (p, s) => s >= 50 && isRunFriendly(p),
  );
  const runBest = runGroups
    .map((g) => buildWindow('run', g, prayerHours))
    .sort((a, b) => b.meanScore - a.meanScore)[0] || null;

  // Filter dupes (same range): keep the one with the more specific kind.
  const windows: OutdoorWindow[] = [];
  if (walkBest) windows.push(walkBest);
  if (strollerBest && (!walkBest || strollerBest.startHour !== walkBest.startHour)) windows.push(strollerBest);
  if (runBest && !windows.some((w) => w.startHour === runBest.startHour)) windows.push(runBest);

  // Hydration nudge
  const fireHydration = tempPeak > 30 && uvPeak > 7;
  let beforeKey: OutdoorIntel['hydrationNudge']['before'] = undefined;
  if (fireHydration) {
    // Prefer Dhuhr if still ahead, else Asr.
    const dhuhr = todayTimes?.dhuhr;
    if (dhuhr instanceof Date && dhuhr.getTime() > Date.now()) beforeKey = 'dhuhr';
    else beforeKey = 'asr';
  }

  const indoorDay = windows.length === 0 || (walkBest?.meanScore ?? 0) < 40;

  return {
    generatedAt: Date.now(),
    windows,
    hydrationNudge: { fire: fireHydration, before: beforeKey, tempPeak, uvPeak },
    indoorDay,
    city: location.city,
  };
}
